import { useState } from "react";
import { confirm } from "devextreme/ui/dialog";
import { PhaseDto } from "../../../../../dtos/PhaseDto";
import { createResubmission } from "../../../../../api/v2/PhasesApi";
import { useAppContext } from "../../../AppContext";
import { PermissionType } from "../../../../../dtos/PermissionType";
import { allowsNewCycle, cycleVocabulary } from "../taskCycle";
import { checkPhasePermission } from "../../../Utils";

export interface ITaskCycleControlProps {
    // Null while the phase is still loading: nothing can be started until it arrives.
    phase: PhaseDto | null;
    // Needed for the permission check: external users resolve their role per grant.
    grantId?: string;
    // Called after a new cycle is created (with its id) so the phase and its subtask list reload
    // with the new cycle in place.
    onResubmitted: (newSubmissionId?: string) => void | Promise<void>;
}

export function useTaskCycleControlController({ phase, grantId, onResubmitted }: ITaskCycleControlProps) {
    const app = useAppContext();
    const [busy, setBusy] = useState(false);

    const vocabulary = cycleVocabulary;

    // The template must opt in AND the caller must hold the Resubmit permission. Without the
    // permission check the button renders for everyone and the server rejects the click, so the
    // user is offered an action that cannot succeed (ticket 1023).
    const canStartNewCycle = !!phase && allowsNewCycle(phase)
        && checkPhasePermission(phase, app, PermissionType.Resubmit, true, grantId);

    const onClickStartCycle = async () => {
        if (!phase) return;
        const confirmed = await confirm(
            vocabulary.confirmPrompt(phase.defaultPhase?.name ?? "this task"),
            vocabulary.confirmTitle
        );
        if (!confirmed) return;

        setBusy(true);
        // Global panel for the whole operation - the create call plus the phase/step refresh that
        // follows - so the user gets immediate feedback and the scoped tasklist spinner (gated on
        // this being visible) does not stack a second overlay on top.
        app.showLoadPanel(vocabulary.startingMessage);
        try {
            const result = await createResubmission(phase.id);
            if (result?.error) {
                app.errorToast(result.error.message);
                return;
            }
            app.infoToast(vocabulary.startedMessage);
            await onResubmitted(result?.data?.id);
        } finally {
            app.hideLoadPanel();
            setBusy(false);
        }
    };

    return {
        canStartNewCycle,
        startActionLabel: vocabulary.startAction,
        busy,
        onClickStartCycle,
    };
}
